'use client'
import { PrismicRichText } from "@prismicio/react";
import { useEffect, useRef, useState } from "react";
import { isFilled } from "@prismicio/client";


/**
 * @typedef {import("@prismicio/client").Content.FaqAccordionSlice} FaqAccordionSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<FaqAccordionSlice>} FaqAccordionProps
 * @type {import("react").FC<FaqAccordionProps>}
 */

const FaqItem = ({ faq, index, toggleItem, isOpen }) => {
  const contentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("0px");

  useEffect(() => {
    if (isOpen && contentRef.current) {
      setMaxHeight(`${contentRef.current.scrollHeight}px`);
    } else {
      setMaxHeight("0px");
    }
  }, [isOpen]);

  return (
    <div className="border-t-1 first:border-t-0 py-5 first:pt-0 last:pb-0 border-[#E4E4E3]">
      <div
        onClick={() => toggleItem(index)}
        className={`text-left relative cursor-pointer transition-all duration-300`}
      >
        {isFilled.richText(faq.question) && (
          <PrismicRichText
            field={faq.question}
            components={{
              heading3: ({ children }) => (
                <h3 className="text-question pr-10 md:pr-20">
                  {children}
                </h3>
              ),
              heading4: ({ children }) => (
                <h4 className="text-question pr-10 md:pr-20">
                  {children}
                </h4>
              ),
            }}
          />
        )}
        <div className="absolute right-0 top-1 transition-transform duration-300"
          style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)" }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18.707 8.29289C19.0975 8.68342 19.0975 9.31643 18.707 9.70696L12.707 15.707C12.3165 16.0975 11.6834 16.0975 11.2929 15.707L5.29292 9.70696C4.9024 9.31643 4.9024 8.68342 5.29292 8.29289C5.68345 7.90237 6.31646 7.90237 6.70698 8.29289L12 13.5859L17.2929 8.29289C17.6834 7.90237 18.3165 7.90237 18.707 8.29289Z" fill="#063A33" />
          </svg>
        </div>
      </div>

      {/* Réponse */}
      <div
        style={{ maxHeight }}
        className="overflow-hidden transition-all duration-500 ease-in-out"
        ref={contentRef}
      >
        <div>
          <PrismicRichText field={faq.answer} components={{
            paragraph: ({ children }) => (
              <p className="pt-5 lg:pr-28">
                {children}
              </p>
            ),
          }}
          />
        </div>
      </div>
    </div>
  );
};


const FaqAccordion = ({ slice }) => {
  const { title, items } = slice.primary;
  const [openIndex, setOpenIndex] = useState(null);

  const toggleItem = (index) => {
    setOpenIndex(index === openIndex ? null : index);
  };

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="framing mt-20 mb-34 md:mt-28 md:mb-44 bg-white overflow-hidden bg-white text-dark-green"
    >
      <div className="flex flex-col gap-8 max-w-325 mx-auto">

        {isFilled.richText(title) && (
          <PrismicRichText
            field={title}
            components={{
              heading1: ({ children }) => (
                <h1 className="h2">
                  {children}
                </h1>
              ),
              heading2: ({ children }) => (
                <h2 className="h2">
                  {children}
                </h2>
              ),
              heading3: ({ children }) => (
                <h3 className="h2">
                  {children}
                </h3>
              ),
            }}
          />
        )}
        {isFilled.richText(items) && (
          <div className="flex flex-col bg-light-gray rounded-2xl p-8">
            {items.map((faq, index) => (
              <FaqItem
                key={index}
                faq={faq}
                index={index}
                toggleItem={toggleItem}
                isOpen={openIndex === index}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default FaqAccordion;
